import axios, { AxiosError, AxiosInstance } from 'axios'
import debounce from 'lodash/debounce'

import { storage } from 'Shared/utils/storage'
import { getCurrentUrl } from 'Shared/utils/url/helpers'

import {
	STORAGE_LAST_REQUEST_TIMESTAMP,
	STORAGE_USER_TOKEN,
	TIMEOUT_ERROR_MESSAGE
} from 'Core/constants'
import { getEnv } from 'Core/env'
import Auth from 'Core/utils/auth'

const env = getEnv()

declare module 'axios' {
	export interface AxiosResponse<T> extends Promise<T> {}
}

const TIMEOUT = 180000

const timeoutConfig = {
	timeout: TIMEOUT,
	timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
	transitional: {
		clarifyTimeoutError: true
	}
}

const httpBaseClient = axios.create({
	baseURL: env.REACT_APP_BASE_URL,
	...timeoutConfig
})

const httpIdentityClient = axios.create({
	baseURL: env.REACT_APP_OIDC_AUTHORITY,
	...timeoutConfig
})

const axiosInstanceArray = [httpBaseClient, httpIdentityClient]

const setLastRequestTimestamp = debounce(
	() => storage.set(STORAGE_LAST_REQUEST_TIMESTAMP, Math.floor(Date.now() / 1000).toString()),
	300
)

export function axiosInstances(axiosInstanceArray: AxiosInstance[], AuthInstance?: Auth) {
	const userManager = AuthInstance?.userManager
	for (let i = 0; i < axiosInstanceArray.length; i++) {
		// signoutRedirect is async function and if call it twice, "post_logout_redirect_uri" will be lost
		let isLogOutProcessed = false
		axiosInstanceArray[i].interceptors.request.use(async (config) => {
			// saving last request time. User will be forced logout due to inactivity based on it
			setLastRequestTimestamp()
			const token = storage.get(STORAGE_USER_TOKEN) as string
			if (!token) {
				// if token was removed manualy or by logout from another tab - force logaout for any request
				await userManager?.signoutRedirect({
					post_logout_redirect_uri: getCurrentUrl()
				})
			}
			config.headers!.Authorization = `Bearer ${token}`
			return config
		})

		axiosInstanceArray[i].interceptors.response.use(
			(response) => {
				// Any status code that lie within the range of 2xx cause this function to trigger
				// Do something with response data
				return response.data
			},
			async (error: AxiosError) => {
				if (error.response?.status === 401 && !isLogOutProcessed) {
					isLogOutProcessed = true
					await AuthInstance?.clearAuthStorage()
					await userManager?.signoutRedirect({
						post_logout_redirect_uri: getCurrentUrl()
					})
					return
				}

				// Any status codes that falls outside the range of 2xx cause this function to trigger
				// Do something with response error
				if (axios.isCancel(error)) {
					// Provide cancel error, when request is aborted
					throw error
				}
				return Promise.reject(error)
			}
		)
	}
}

export const setAxiosInstance = (auth?: Auth) => {
	setLastRequestTimestamp()
	axiosInstances(axiosInstanceArray, auth)
}

export const httpUserClient = httpIdentityClient
export default httpBaseClient
